import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Divider,
  IconButton
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Close, Add } from "@mui/icons-material";
import PropTypes from "prop-types";
import moment from "moment";
import { getCurrentTime } from "../utils/assistantUtils";
const RCESB30Modal = React.forwardRef(({ setRceSb30Intent, originatingRequestId, requestId, handleModuleSubmit, handleStop, setMessages, applicationNumbers }, ref) => {
  const initialValues = {
    application_nums: applicationNumbers || "",
    submission: {
      previouslySubmitted: false,
      appealArguments: false,
      appealDate: null,
      othersPreviouslySubmittedCheck: false,
      othersPreviouslySubmitted: "",

      enclosed: false,
      amendment: false,
      ids: false,
      affidavits: false,
      othersEnclosedCheck: false,
      othersEnclosed: ""
    },
    miscellaneous: {
      suspension: false,
      months: "",
      othersMiscellaneousCheck: false,
      othersMiscellaneous: ""
    },
    fees: {
      depositAccountNoCheck: false,
      depositAccountNo: ""
    },
    signature: {
      signatureType: "patent-practitioner",
      addSignatures: false,
      practitionerName: "",
      practitionerRegistrationNumber: "",
      applicants: [{ name: "" }]
    }
  }
  const [rceSb30Data, setRceSb30Data] = useState(initialValues);

  // Update application numbers when prop changes
  useEffect(() => {
    if (applicationNumbers) {
      setRceSb30Data(prev => ({
        ...prev,
        application_nums: applicationNumbers
      }));
    }
    return () => {
      setRceSb30Data(initialValues)
    }
  }, [applicationNumbers]);

  const isSubmitDisabled = () => {
    const { signatureType, practitionerName, practitionerRegistrationNumber, applicants } = rceSb30Data.signature;
    if (signatureType === "patent-practitioner") {
      return !practitionerName.trim() || !practitionerRegistrationNumber.trim();
    }
    if (signatureType === "applicant") {
      return applicants.length === 0 || applicants.some(a => !a.name.trim());
    }
    return false;
  };

  const handleSubmit = async () => {
    const finalApplicationNums = rceSb30Data.application_nums || applicationNumbers || "";
    const dataToSubmit = {
      ...rceSb30Data,
      application_nums: finalApplicationNums
    };

    const formattedString = JSON.stringify(dataToSubmit);
    const message = `Please prepare SB-30 Request for Continued Examination (RCE) form with the following details:\n\n${formattedString}`;

    handleModuleSubmit({
      textInput: "RCE Transmittal (EFS)",
      message,
      originatingRequestId,
      requestId,
      largeSlotData: formattedString
    });
    setRceSb30Intent(false);
  };

  const handleInputChange = (section, field, value) => {
    setRceSb30Data(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSignatureChange = (value) => {
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        signatureType: value
      }
    }));
  };

  const handleSignatureCheckbox = (checked) => {
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        addSignatures: checked
      }
    }));
  };

  const handlePractitionerChange = (field, value) => {
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        [field]: value
      }
    }));
  };

  const handleApplicantChange = (index, value) => {
    const updatedApplicants = [...rceSb30Data.signature.applicants];
    updatedApplicants[index].name = value;
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        applicants: updatedApplicants
      }
    }));
  };

  const addApplicant = () => {
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        applicants: [...prev.signature.applicants, { name: "" }]
      }
    }));
  };

  const removeApplicant = (index) => {
    const updatedApplicants = rceSb30Data.signature.applicants.filter((_, i) => i !== index);
    setRceSb30Data(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        applicants: updatedApplicants
      }
    }));
  };

  const handleReset = () => {
      setRceSb30Data({
    ...initialValues,
    application_nums: applicationNumbers || ""
  });
  };

  const submissionLabel1 = "Previously submitted. If a final Office action is outstanding, any amendments filed after the final Office action may be considered as a submission even if this box is not checked.";
  const previouslySubmittedLabel = "Consider the arguments in the Appeal Brief or Reply Brief previously filed on";
  const miscellaneousLabel1 =
    <>
      Suspension of action on the above-identified application is requested
      under 37 CFR 1.103(c) for a period of months
      <br />
      (Period of suspension shall not exceed 3 months; Fee under 37 CFR
      1.17(i) required)
    </>
    ;
  const feeLabel1 = "The RCE fee under 37 CFR 1.17(e) is required by 37 CFR 1.114 when the RCE is filed. The Director is hereby authorized to charge any underpayment of fees, or credit any overpayments, to Deposit";

  return (
<Box
  ref={ref}
  tabIndex={-1}
  sx={{
    width: "100%",
    height: "92vh",
    background: "#fefefe",
    display: "flex",
    flexDirection: "column",
    padding: "1.5rem",
    gap: "1rem",

    '&:focus': {
      outline: 'none',
    },
  }}
>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => {
            if (setRceSb30Intent) setRceSb30Intent(false);
            if (handleStop) handleStop();
            const time = getCurrentTime();
            if (setMessages) setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled.", time }]);
          }}
          sx={{
            width: "fit-content",
            ":hover": {
              background: "transparent",
            },
          }}
        >
          <Close />
        </Button>
      </Box>

      <Box>
        <Typography fontSize={"1.5rem"} fontWeight="600" color={"black"} sx={{ mb: 2, mr: 1 }}>
          REQUEST FOR CONTINUED EXAMINATION (RCE) TRANSMITTAL (Submitted Only via EFS-Web)
        </Typography>
      </Box>
      <Box sx={{ height: "60vh", overflowY: "auto", padding: "1rem" }}>
        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />
        <Box sx={{ mb: 3 }} fontSize={"1.1rem"} fontWeight="600" >
          <Typography>This is a Request for Continued Examination (RCE) under 37 CFR 1.114 of the above-identified application. Request for Continued Examination (RCE) practice under 37 CFR 1.114 does not apply to any utility or plant application filed prior to June 8, 1995, or to any design application. The Instruction Sheet for this form is located at WWW.USPTO.GOV</Typography>
        </Box>
        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />
        {/* Submission Section */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            SUBMISSION REQUIRED UNDER 37 CFR 1.114
          </Typography>
          <Typography>
            Note: If the RCE is proper, any previous filed unentered amendments and amendments enclosed with the rce will be entered in the order in which they were filed unless applicant instructs otherwise. If applicant does not wish to have any previously filed unentered amendments(s) entered, applicant must request non-entry of such amendment(s).
          </Typography>
          <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rceSb30Data.submission.previouslySubmitted}
                  onChange={(e) => handleInputChange("submission", "previouslySubmitted", e.target.checked)}
                  sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                />
              }
              label={submissionLabel1}
            />

            {rceSb30Data.submission.previouslySubmitted && (
              <Box sx={{ ml: 4, display: "flex", flexDirection: "column", gap: 2, mt: 1, mb: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2}}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.appealArguments}
                      onChange={(e) => handleInputChange("submission", "appealArguments", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label={previouslySubmittedLabel}
                />

                {rceSb30Data.submission.appealArguments && (
                  <Box sx={{ ml: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Select Date"
                        value={rceSb30Data.submission.appealDate ? moment(rceSb30Data.submission.appealDate) : null}
                        onChange={(newValue) => {
                          const dateString = newValue ? newValue.format("YYYY-MM-DD") : null;
                          handleInputChange("submission", "appealDate", dateString);
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                        inputFormat="YYYY-MM-DD"
                        maxDate={moment(new Date())}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              </Box>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2}}>
                  <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.othersPreviouslySubmittedCheck}
                      onChange={(e) => handleInputChange("submission", "othersPreviouslySubmittedCheck", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label="Other"
                />

                {rceSb30Data.submission.othersPreviouslySubmittedCheck && (
                  <Box sx={{ ml: 4 }}>
                    <TextField
                      size="small"
                      value={rceSb30Data.submission.othersPreviouslySubmitted}
                      onChange={(e) => handleInputChange("submission", "othersPreviouslySubmitted", e.target.value)}
                      placeholder="Specify other previously submitted"
                      fullWidth
                    />
                  </Box>
                )}
                </Box>
              
            
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={rceSb30Data.submission.enclosed}
                  onChange={(e) => handleInputChange("submission", "enclosed", e.target.checked)}
                  sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                />
              }
              label="Enclosed"
            />

            {rceSb30Data.submission.enclosed && (
              <Box sx={{ ml: 4, display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.amendment}
                      onChange={(e) => handleInputChange("submission", "amendment", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label="Amendment/Reply"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.ids}
                      onChange={(e) => handleInputChange("submission", "ids", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label="Information Disclosure Statement (IDS)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.affidavits}
                      onChange={(e) => handleInputChange("submission", "affidavits", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label="Affidavits/Declaration(s)"
                />
                 <Box sx={{ display: "flex", flexDirection: "row", gap: 2}}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rceSb30Data.submission.othersEnclosedCheck}
                      onChange={(e) => handleInputChange("submission", "othersEnclosedCheck", e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                    />
                  }
                  label="Other"
                />
                {rceSb30Data.submission.othersEnclosedCheck && (
                  <Box sx={{ ml: 4 }}>
                    <TextField
                      size="small"
                      value={rceSb30Data.submission.othersEnclosed}
                      onChange={(e) => handleInputChange("submission", "othersEnclosed", e.target.value)}
                      placeholder="Specify other enclosed"
                      fullWidth
                    />
                  </Box>
                )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Miscellaneous Section */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            MISCELLANEOUS
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rceSb30Data.miscellaneous.suspension}
                    onChange={(e) => handleInputChange("miscellaneous", "suspension", e.target.checked)}
                    sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                  />
                }
                label={miscellaneousLabel1}
              />
              {rceSb30Data.miscellaneous.suspension && (
                <TextField
                  size="small"
                  value={rceSb30Data.miscellaneous.months}
                  onChange={(e) => handleInputChange("miscellaneous", "months", e.target.value)}
                  placeholder="Months"
                  sx={{ width: "100px" }}
                />
              )}
            </Box>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rceSb30Data.miscellaneous.othersMiscellaneousCheck}
                  onChange={(e) => handleInputChange("miscellaneous", "othersMiscellaneousCheck", e.target.checked)}
                  sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                />
              }
              label="Other"
            />
            {rceSb30Data.miscellaneous.othersMiscellaneousCheck && (
              <Box sx={{ ml: 4 }}>
                <TextField
                  size="small"
                  value={rceSb30Data.miscellaneous.othersMiscellaneous}
                  onChange={(e) => handleInputChange("miscellaneous", "othersMiscellaneous", e.target.value)}
                  placeholder="Specify other miscellaneous"
                  fullWidth
                />
              </Box>
            )}
            </Box>

          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Fees Section */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            FEES
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={rceSb30Data.fees.depositAccountNoCheck}
                onChange={(e) => handleInputChange("fees", "depositAccountNoCheck", e.target.checked)}
                sx={{ "&.Mui-checked": { color: "#005cb9" } }}
              />
            }
            label={feeLabel1}
          />
          
          {rceSb30Data.fees.depositAccountNoCheck && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2,mt:2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500 , lineHeight:'2.5rem'}}
              >
                Account No
              </Typography>

              <TextField
                size="small"
                label="Enter Account Number"
                value={rceSb30Data.fees.depositAccountNo}
                onChange={(e) =>
                  handleInputChange("fees", "depositAccountNo", e.target.value)
                }
              />
            </Box>
          )}
        </Box>


        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Signature Section */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            SIGNATURE OF APPLICANT, ATTORNEY, OR AGENT REQUIRED
          </Typography>

          <FormControl component="fieldset">
            <RadioGroup
              sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}
              value={rceSb30Data.signature.signatureType}
              onChange={(e) => handleSignatureChange(e.target.value)}
            >
              <FormControlLabel
                value="patent-practitioner"
                control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                label="Patent Practitioner Signature"
              />
              <FormControlLabel
                value="applicant"
                control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                label="Applicant Signature"
              />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ mb: 3, borderColor: "#333", borderWidth: 1.5 }} />

          <Typography sx={{ mb: 2 }}>Signature of registered U.S. patent practitioner</Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={rceSb30Data.signature.addSignatures}
                onChange={(e) => handleSignatureCheckbox(e.target.checked)}
                sx={{ "&.Mui-checked": { color: "#005cb9" } }}
              />
            }
            label="Would you like to add signatures?"
          />

          {rceSb30Data.signature.signatureType === "patent-practitioner" && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
              <TextField
                size="small"
                label="Name *"
                placeholder="Name"
                value={rceSb30Data.signature.practitionerName}
                onChange={(e) => handlePractitionerChange("practitionerName", e.target.value)}
              />
              <TextField
                size="small"
                label="Registration Number *"
                placeholder="Registration Number"
                value={rceSb30Data.signature.practitionerRegistrationNumber}
                onChange={(e) => handlePractitionerChange("practitionerRegistrationNumber", e.target.value)}
              />
            </Box>
          )}

          {rceSb30Data.signature.signatureType === "applicant" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              {rceSb30Data.signature.applicants.map((applicant, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label={`Applicant ${index + 1} *`}
                    placeholder={`Applicant ${index + 1}`}
                    value={applicant.name}
                    onChange={(e) => handleApplicantChange(index, e.target.value)}
                  />
                  {rceSb30Data.signature.applicants.length > 1 && (
                    <IconButton onClick={() => removeApplicant(index)} sx={{ mt: 0.5 }}>
                      <Close />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addApplicant}
                  sx={{ width: "fit-content" }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}
        </Box>

      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, pt: 2 }}>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled()}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
});

RCESB30Modal.propTypes = {
  setRceSb30Intent: PropTypes.func,
  originatingRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleModuleSubmit: PropTypes.func,
  handleStop: PropTypes.func,
  setMessages: PropTypes.func,
  applicationNumbers: PropTypes.string
};

export default RCESB30Modal;